import React, { useCallback, useMemo } from "react";
import { Button, Card, message, Row, Spin } from "antd";
import { ISSOUser } from "../../services/typings";
import WhiteSpace from "../../../../components/base/WhiteSpace";
import Container from "../../components/Container";
import Helmet from "react-helmet";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "locales/components/LanguageSwitcher";
import { Box } from "components/base";
import ThemeSwitcher from "themes/components/ThemeSwitcher";
import UserList from "./components/UserList";
import useSizeMediaQuery from "hooks/useSizeMediaQuery";
import { useHistory, useLocation } from "react-router";
import { lazy } from "utils/route";

interface IProps {
  onLogin: () => void;
  onLoginWithExistUser: (user: ISSOUser, loggedOut?: boolean) => void;
  loading?: boolean;
  usersLoading?: boolean;
  users?: ISSOUser[];
}

const Component = lazy(() => import("layouts/PrivateLayout"));

const Login = (props: IProps) => {
  const {
    loading = false,
    usersLoading = false,
    onLogin,
    users,
    onLoginWithExistUser,
  } = props;

  const { t } = useTranslation();

  const { push } = useHistory();

  const handleLogin = useCallback(
    async (user?: ISSOUser, loggedOut?: boolean) => {
      try {
        Component?.preload();
        if (!!user && !loggedOut) {
          await onLoginWithExistUser(user, loggedOut);
        } else {
          await onLogin();
        }
      } catch (error) {
        message.error(error?.error || error?.message || t("common.errors.msg"));
      }
    },
    [onLogin, t, onLoginWithExistUser]
  );

  const handleLoginWithExistUser = useCallback(
    (user: ISSOUser, loggedOut?: boolean) => {
      handleLogin(user, loggedOut);
    },
    [handleLogin]
  );

  const size = useSizeMediaQuery();

  const isSmall = useMemo(() => ["xs"].includes(size), [size]);

  return (
    <Box
      width="100vw"
      height="100vh"
      flex={1}
      display="flex"
      alignItems="center"
      justifyContent="center"
      position="relative"
    >
      <Helmet>
        <title>{t("auth.login.login")}</title>
      </Helmet>
      <Container />
      <Card
        style={{
          width: isSmall ? "100%" : 400,
          zIndex: 3,
          padding: 16,
          height: isSmall ? "100%" : undefined,
          maxHeight: "100%",
          overflowY: "auto",
        }}
        bordered={false}
      >
        <Row align="middle" justify="center">
          <img
            src={require("assets/logo.png")}
            style={{ height: 50 }}
            alt="Tiki"
          />
          <WhiteSpace horizontal />
          <span
            style={{
              color: "#1890ff",
              fontFamily: "Avenir, Helvetica, Arial, sans-serif",
              fontWeight: "bold",
              fontSize: 24,
            }}
          >
            {t("auth.login.tiki_affiliate")}
          </span>
        </Row>
        <Box
          flex={1}
          textAlign="center"
          justifyContent="center"
          alignItems="center"
          display="flex"
          fontSize={18}
          fontWeight="bold"
          mt={3}
        >
          {t("auth.login.form_title")}
        </Box>

        {usersLoading ? (
          <Spin spinning>
            <Box flex={1} height="120px"></Box>
          </Spin>
        ) : (
          <>
            {users?.length ? (
              <>
                <WhiteSpace size="lg" />
                <Box width="100%" textAlign="center">
                  <h2 style={{ marginBottom: 0, fontSize: 16 }}>
                    {t("auth.login.choose_an_account")}
                  </h2>
                  <WhiteSpace size="sm" />
                  <span>
                    {t("auth.login.to_continue_to")}{" "}
                    <b
                      style={{
                        color: "#1890ff",
                        fontFamily: "Avenir, Helvetica, Arial, sans-serif",
                      }}
                    >
                      {t("auth.login.tiki_affiliate")}
                    </b>
                  </span>
                </Box>
                <Spin spinning={false}>
                  <UserList
                    data={users}
                    onItemClick={handleLoginWithExistUser}
                    onUseAnotherAccountClick={handleLogin}
                  />
                </Spin>
              </>
            ) : (
              <>
                <Box py={3} style={{ width: "100%" }}>
                  <Button
                    type="primary"
                    onClick={() => handleLogin()}
                    style={{ width: "100%" }}
                    loading={false}
                    size="large"
                  >
                    {t("auth.login.login")}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}

        <Box
          flex={1}
          flexDirection="row"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          display="flex"
        >
          <LanguageSwitcher style={{ marginRight: 8 }} />
          <ThemeSwitcher style={{ marginLeft: 8 }} />
        </Box>
      </Card>
    </Box>
  );
};

export default React.memo(Login);
